<script setup>
import { ref, onMounted, defineEmits, defineProps, computed, watch, nextTick } from 'vue'
import { useExplorerStore } from '@/store/ExplorerStore';
import { filterByTags, filterByBadges, fetchTagTypes, fetchLLMs } from './services/exploreService';

const explorerStore = useExplorerStore();

const emit = defineEmits(['filters-applied', 'filters-cleared'])

const props = defineProps({
  listings: {
    type: Array,
    default: () => ([]),
  },
});

const activeTab = ref('main')

const tabs = [
    { id: 'main', label: 'Main' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'occupations', label: 'Occupations' }
]

const tags = ref([])
const selectedTags = ref([])
const expandedSections = ref({})

const sellerBadges = ref([])
const selectedSellerBadges = ref([])

const syncWithStore = () => {
    const currentFilterKey = tabToFilterKey[activeTab.value]
    if (currentFilterKey && explorerStore.filters[currentFilterKey] && tags.value.length > 0) {
        const storeTagNames = explorerStore.filters[currentFilterKey]
        if (Array.isArray(storeTagNames)) {
            selectedTags.value = storeTagNames
                .map(tagName => {
                    const tag = tags.value.find(
                        t => t.name === tagName && 
                             t.category === filterConfig[currentFilterKey]?.category
                    )
                    return tag?.id
                })
                .filter(Boolean)
        } else {
            selectedTags.value = []
        }
    } else {
        selectedTags.value = []
    }
    
    selectedModelName.value = explorerStore.filters.eval_model_name || ''
    selectedModelScoreRange.value = explorerStore.filters.eval_score_range || ''
    selectedCreatorExpertise.value = explorerStore.filters.seller_badges || []
    selectedTaskCount.value = explorerStore.filters.eval_total_tasks || ''
    selectedCostPerRun.value = explorerStore.filters.eval_cost_per_run || ''
    selectedRunTimeMax.value = explorerStore.filters.eval_run_time_max || ''
    selectedSize.value = explorerStore.filters.size || ''
    selectedDate.value = explorerStore.filters.date_range || ''
    priceRange.value = explorerStore.filters.price_range || ''
}

watch(() => explorerStore.filters, () => {
    if (tags.value.length > 0) {
        syncWithStore()
    }
}, { deep: true })

watch(activeTab, () => {
    if (tags.value.length > 0) {
        syncWithStore()
    }
})

const toggleSection = (type) => {
    expandedSections.value[type] = !expandedSections.value[type]
}

const groupTagsByType = (tagsArray) => {
    const grouped = {}
    tagsArray.forEach(tag => {
        const type = tag.tag_type?.type || 'Other'
        if (!grouped[type]) {
            grouped[type] = []
        }
        grouped[type].push(tag)
    })
    return grouped
}

const modalityTags = computed(() => tags.value.filter(tag => ['Modality', 'Main'].includes(tag.category)))
const capabilityTags = computed(() => tags.value.filter(tag => ['Use Case', 'Capabilities', 'Capability'].includes(tag.category)))
const occupationTags = computed(() => tags.value.filter(tag => ['Domain', 'Occupation', 'Occupations'].includes(tag.category)))

const groupedModalityTags = computed(() => groupTagsByType(modalityTags.value))
const groupedCapabilityTags = computed(() => groupTagsByType(capabilityTags.value))
const groupedOccupationTags = computed(() => groupTagsByType(occupationTags.value))
const socOccupationTags = ref([])

// Tag types (Major Groups) and Detailed Occupations
const tagTypes = ref([])
const detailedOccupations = ref([])
const ALL_GROUPS = 'ALL_GROUPS'
const selectedTagType = ref(ALL_GROUPS)

const selectedModelName = ref('')
const selectedModelScoreRange = ref('')
const selectedCreatorExpertise = ref([])
const selectedTaskCount = ref('')
const selectedCostPerRun = ref('')
const selectedRunTimeMax = ref('')
const selectedSize = ref('')
const selectedDate = ref('')
const priceRange = ref('')

const sizeOptions = [
    { id: 'less_than_1gb', label: '< 1GB'},
    { id: '1gb_to_10gb', label: '1GB - 10GB'},
    { id: 'more_than_10gb', label: '> 10GB'}
]

const dateOptions = [
    { id: 'last_24h', label: '< 24H'},
    { id: 'last_7d', label: 'Last 7d'},
    { id: 'last_30d', label: 'Last 30d'}
]

const priceOptions = [
    { id: 'less_than_1k', label: '< $1k'},
    { id: '1k_to_10k', label: '$1k - $10k'},
    { id: 'more_than_10k', label: '> $10k'}
]

const modelOptions = ref([])

const modelScoreOptions = [
    { id: '', label: 'Any score', sublabel: 'no score filter', isAny: true },
    { id: 'severe_failures', label: '≤ 20%', sublabel: 'severe failures' },
    { id: 'major_gaps', label: '20–40%', sublabel: 'major gaps' },
    { id: 'borderline', label: '40–60%', sublabel: 'borderline' },
    { id: 'solid', label: '60–80%', sublabel: 'solid' },
    { id: 'strong', label: '≥ 80%', sublabel: 'strong' }
]

const averageDifficultyOptions = [
    { id: 'severe_failures', label: '≤ 20%', sublabel: 'severe failures' },
    { id: 'major_gaps', label: '20–40%', sublabel: 'major gaps' },
    { id: 'borderline', label: '40–60%', sublabel: 'borderline' },
    { id: 'solid', label: '60–80%', sublabel: 'solid' },
    { id: 'strong', label: '≥ 80%', sublabel: 'strong' }
]

const expertiseOptions = [
    { id: 'phd_verified', label: 'PhD Verified' },
    { id: 'domain_expert', label: 'Domain Expert' },
    { id: 'portex_core_team', label: 'Portex Core Team' },
    { id: 'hles_contributor', label: 'Humanity’s Last Exam Contributor' },
    { id: 'Physics', label: 'Physical Sciences' }
]

const taskCountOptions = [
    { id: 'micro', label: 'Micro', sublabel: '< 10 tasks' },
    { id: 'standard', label: 'Standard', sublabel: '10–100 tasks' },
    { id: 'scale', label: 'Scale', sublabel: '100+ tasks' }
]

const costPerRunOptions = [
    { id: 'cheap', label: '< $10' },
    { id: 'affordable', label: '$10–$50' },
    { id: 'medium', label: '$50–$100' },
    { id: 'expensive', label: '> $100' }
]

const runTimeMaxOptions = [
    { id: 'fast', label: 'Fast', sublabel: '< 1 min' },
    { id: 'medium', label: 'Medium', sublabel: '2-10 min' },
    { id: 'long', label: 'Long', sublabel: '10+ min' }
]

const tabToFilterKey = {
    'main': 'modality',
    'capabilities': 'use_case',
    'occupations': 'domain'
}

const fetchTags = async () => {
    try {
        const response = await filterByTags()
        tags.value = response.data
        socOccupationTags.value = tags.value
            .filter(tag => tag.tag_type?.is_eval === true)
            .sort((a, b) => a.name.localeCompare(b.name))
        
        if (selectedTagType.value === ALL_GROUPS) {
            detailedOccupations.value = tags.value
                .filter(tag => tag.tag_type?.is_eval === true)
                .sort((a, b) => a.name.localeCompare(b.name))
        }
        nextTick(() => {
            if (tags.value.length > 0) {
                syncWithStore()
            }
        })
    } catch (error) {
        console.error('Error fetching tags:', error)
    }
}

const selectedFiltersCount = computed(() => {
    let count = 0
    
    Object.values(tabToFilterKey).forEach(filterKey => {
        if (explorerStore.filters[filterKey] && Array.isArray(explorerStore.filters[filterKey])) {
            count += explorerStore.filters[filterKey].length
        }
    })
    
    if (selectedModelName.value) count++
    if (selectedModelScoreRange.value) count++
    if (selectedCreatorExpertise.value.length > 0) count++
    if (selectedRunTimeMax.value) count++
    if (explorerStore.filters.eval_average_difficulty) count++
    if (selectedTaskCount.value) count++
    if (selectedCostPerRun.value) count++
    if (selectedSize.value) count++
    if (selectedDate.value) count++
    if (priceRange.value) count++
    
    return count
})

const fetchSellerBadges = async () => {
    try {
        const response = await filterByBadges()
        sellerBadges.value = response.data;
    } catch (error) {
        console.error('Error fetching seller badges:', error)
    }
}

const fetchTagTypesList = async () => {
    try {
        const response = await fetchTagTypes()
        const allTagTypes = response.data || []
        
        tagTypes.value = allTagTypes.filter(tagType => {
            if (typeof tagType === 'object' && tagType !== null) {
                return tagType.is_eval === true
            }
            return false
        })
    } catch (error) {
        console.error('Error fetching tag types:', error)
    }
}

const fetchModels = async () => {
    try {
        const response = await fetchLLMs()
        const llms = response.data || []
        
        modelOptions.value = llms.map(llm => ({
            id: llm.name,
            label: llm.name,
            icon: llm.icon || null
        }))
    } catch (error) {
        console.error('Error fetching LLMs:', error)
        modelOptions.value = []
    }
}

const handleTagTypeClick = (tagTypeValue) => {
    
    if (tagTypeValue === ALL_GROUPS) {
        if (selectedTagType.value === ALL_GROUPS) {
            selectedTagType.value = null
            detailedOccupations.value = []
        } else {
            selectedTagType.value = ALL_GROUPS
            
            detailedOccupations.value = tags.value.filter(tag => tag.tag_type?.is_eval === true)
        }
        return
    }
    
    const tagType = typeof tagTypeValue === 'string' 
        ? tagTypeValue 
        : (tagTypeValue?.type || tagTypeValue?.name || tagTypeValue)
    
    // Toggle selection
    if (selectedTagType.value === tagType) {
        selectedTagType.value = null
        detailedOccupations.value = []
    } else {
        selectedTagType.value = tagType
        detailedOccupations.value = tags.value
            .filter(tag => {
                const tagTypeFromTag = tag.tag_type?.type || 'Other'
                return tagTypeFromTag === tagType
            })
            .sort((a, b) => a.name.localeCompare(b.name))
    }
}

const clearAllFilters = () => {
  explorerStore.clearFilters()

  if (tags.value.length > 0) {
      syncWithStore()
  }
  expandedSections.value = {}

  emit('filters-cleared')
}

const filterConfig = {
  modality: { category: 'Modality', filterKey: 'modality' },
  use_case: { category: 'Use Case', filterKey: 'use_case' },
  domain: { category: 'Domain', filterKey: 'domain' },
  seller_badges: { category: 'Creator Expertise', filterKey: 'seller_badges' },
  eval_average_difficulty: { category: 'Eval Difficulty', filterKey: 'eval_average_difficulty' },
  eval_model_name: { category: 'Model', filterKey: 'eval_model_name' },
  eval_score_range: { category: 'Model score', filterKey: 'eval_score_range' },
  eval_total_tasks: { category: 'Task Count', filterKey: 'eval_total_tasks' },
  eval_cost_per_run: { category: 'Cost per Run', filterKey: 'eval_cost_per_run' }
}



const handleFilterClick = (key, value, isMulti = false) => {
    const current = explorerStore.filters[key] || (isMulti ? [] : '')

    let updated
    if (isMulti && Array.isArray(current)) {
        updated = current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value]
    } else {
        updated = current === value ? '' : value
    }

    explorerStore.setFilter(key, updated)
    
    // Reset model score to 'any score' when model is deselected
    if (key === 'eval_model_name' && updated === '') {
        explorerStore.setFilter('eval_score_range', '')
    }
    
    if (tags.value.length > 0) {
        syncWithStore()
    }

    emit('filters-applied', explorerStore.filters)
}

onMounted(async () => {
    await fetchTags()
    await fetchSellerBadges()
    await fetchTagTypesList()
    await fetchModels()
    if (tags.value.length > 0) {
        syncWithStore()
    }
})
</script>
<template>
<div class="explore-sidebar">
    <div class="explore-sidebar__panel">
        <div class="explore-sidebar__header">
            <div class="explore-sidebar__header-row">
                
                <span v-if="selectedFiltersCount > 0" class="explore-sidebar__selected-count mb-4">
                    Selected Filters ({{ selectedFiltersCount }})
                </span>
                <div class="explore-sidebar__header-actions ">
                    <button v-if="selectedFiltersCount > 0" @click="clearAllFilters" class="explore-sidebar__clear-btn mb-4">
                        <span>Clear all</span>
                        <i class="uil uil-redo"></i>
                    </button>
                </div>
            </div>

            <!-- Tabs -->
            <div class="explore-sidebar__tabs">
                <button v-for="tab in tabs" :key="tab.id" 
                    @click="activeTab = tab.id"
                    :class="[
                        'explore-sidebar__tab',
                        activeTab === tab.id ? 'explore-sidebar__tab--active' : 'explore-sidebar__tab--inactive'
                    ]">
                    {{ tab.label }}
                </button>
            </div>
        </div>

        <div class="explore-sidebar__content">
            <!-- Main Content -->
            <div v-if="activeTab === 'main'" class="explore-sidebar__group">
                <!-- SOC Occupations -->
                <div class="explore-filter-card">
                    <div class="mb-4">
                        <div class="flex items-center gap-2">
                            <div class="text-white font-semibold text-sm leading-5">SOC Occupations</div>
                            <div class="flex items-center">
                                <i class="mdi mdi-information-outline text-base cursor-help text-white">
                                    <v-tooltip activator="parent" location="top" width="550" text="The Standard Occupational Classification (SOC ) System is the U.S. Bureau of Labor Statistics' taxonomy for organizing jobs into major groups and detailed occupations. On Portex, every eval is mapped to the most appropriate SOC occupation."></v-tooltip>
                                </i>
                            </div>
                        </div>
                        <div class="explore-filter-card__subtitle">Filter evals by real occupations in the U.S. SOC system.</div>
                    </div>

                    <div class="explore-filter-card__body">
                        <div class="explore-chip-row">
                            <template v-for="(tag, index) in socOccupationTags" :key="tag.id">
                                <button v-if="index < 10 || expandedSections['soc_occupations']"
                                    @click="handleFilterClick('use_case', tag.id, true)"
                                    :class="[
                                        'explore-chip',
                                        explorerStore.filters.use_case?.includes(tag.id) ? 'explore-chip--active' : ''
                                    ]"
                                    :style="{
                                        backgroundColor: explorerStore.filters.use_case?.includes(tag.id) ? `${tag.display_color}25` : '',
                                        borderColor: explorerStore.filters.use_case?.includes(tag.id) ? `${tag.display_color}50` : ''
                                    }">
                                    <div class="explore-chip__inner">
                                        <i v-if="tag.display_icon" :class="[tag.display_icon, 'explore-chip__icon']"
                                           :style="{ color: tag.display_color }"></i>
                                           <i v-else :class="[tag.display_icon ? tag.display_icon : 'uil uil-cube', 'explore-chip__icon']"
                                                       :style="{ color: tag.display_color }"></i>
                                        <span class="explore-chip__label explore-chip__label--sm" :title="tag.name">{{ tag.name }}</span>
                                    </div>
                                </button>
                            </template>
                            <button
                                v-if="socOccupationTags.length > 10 && !expandedSections['soc_occupations']"
                                @click="toggleSection('soc_occupations')"
                                class="explore-chip explore-chip--expand">
                                +{{ socOccupationTags.length - 10 }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Average Difficulty -->
                <div class="explore-filter-card">
                    <div class="explore-filter-card__header">
                        <div class="flex items-center gap-2">
                            <div class="text-white font-semibold text-sm leading-5">Eval Average Difficulty</div>
                            <div class="flex items-center">
                                <i class="mdi mdi-information-outline text-base cursor-help text-white">
                                    <v-tooltip activator="parent" location="top" width="550" text="Average scores based on Leaderboards Portex maintains for each eval across a selection of state-of-the-art models."></v-tooltip>
                                </i>
                            </div>
                        </div>
                        <div class="explore-filter-card__subtitle">Filter based on average model performance on these questions.</div>
                    </div>

                    <div class="explore-filter-card__body">
                        <div class="explore-chip-column">
                            <button v-for="opt in averageDifficultyOptions" :key="opt.id"
                                @click="handleFilterClick('eval_average_difficulty', opt.id)"
                                :class="[
                                    'explore-chip w-fit',
                                    'explore-chip--block',
                                    explorerStore.filters.eval_average_difficulty === opt.id ? 'explore-chip--active' : ''
                                ]">
                                <span class="explore-chip__block-text">
                                    <strong>{{ opt.label }}</strong>
                                    <span class="explore-chip__muted">{{ opt.sublabel }}</span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Creator Expertise -->
                <div class="explore-filter-card">
                    <div class="explore-filter-card__header">
                        <div class="flex items-center gap-2">
                            <div class="text-white font-semibold text-sm leading-5">Creator Expertise</div>
                            <div class="flex items-center">
                                <i class="mdi mdi-information-outline text-base cursor-help text-white">
                                    <v-tooltip activator="parent" location="top" width="550" text="All evals on Portex are authored by a whitelisted and verified set of top-decile experts. The Portex team verifies all expert credentials to the best of its ability."></v-tooltip>
                                </i>
                            </div>
                        </div>
                        <div class="explore-filter-card__subtitle">Who authored and maintains the eval</div>
                    </div>

                    <div class="explore-filter-card__body">
                        <div class="explore-chip-row">
                            <button v-for="badge in sellerBadges" :key="badge.id"
                                @click="handleFilterClick('seller_badges', badge.id, true)"
                                :class="[
                                    'explore-chip',
                                    explorerStore.filters.seller_badges?.includes(badge.id) ? 'explore-chip--active' : ''
                                ]">
                                <div class="explore-chip__inner">
                                    <div v-if="badge.badge_image" class="explore-chip__icon-circle">
                                        <img :src="badge.badge_image" :alt="badge.name" />
                                    </div>
                                    <span class="explore-chip__label explore-chip__label--sm" :title="badge.name">{{ badge.name }}</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Task Count -->
            </div>

            <!-- Capabilities Content -->
            <div v-if="activeTab === 'capabilities'" class="explore-sidebar__group">
              <!-- Model performance -->
              <div class="explore-filter-card">
                    <div class="explore-filter-card__header">
                        <div class="flex items-center gap-2">
                            <div class="text-white/95 font-semibold text-sm leading-5">Model Performance</div>
                            <div class="flex items-center">
                                <i class="mdi mdi-information-outline text-base cursor-help text-white">
                                    <v-tooltip activator="parent" location="top" width="600" text="Scores based on Leaderboards Portex maintains for each eval across a selection of state-of-the-art models. Don't see a model you'd like on the leaderboards or want yours evaluated? Send us a message at hello@portexai.com to suggest a model addition."></v-tooltip>
                                </i>
                            </div>
                        </div>
                        <div class="explore-filter-card__subtitle">Choose a model to see its score on each eval and filter to low-scoring evals</div>
                    </div>

                    <div class="explore-filter-card__body">
                        <div class="explore-chip-row mb-1 bg-echoes-background-black rounded-xl p-2">
                            <template v-for="(opt, index) in modelOptions" :key="opt.id">
                                <button v-if="index < 10 || expandedSections['model_performance']"
                                    @click="handleFilterClick('eval_model_name', opt.id)"
                                    :class="[
                                        'explore-chip',
                                        explorerStore.filters.eval_model_name === opt.id ? 'explore-chip--active' : ''
                                    ]">
                                    <div class="explore-chip__inner">
                                        <div class="explore-chip__icon-circle">
                                            <img v-if="opt.icon" :src="opt.icon" :alt="opt.label" />
                                            <i v-else class="mdi mdi-robot"></i>
                                        </div>
                                        <span class="explore-chip__label" :title="opt.label">{{ opt.label }}</span>
                                    </div>
                                </button>
                            </template>
                            <button
                                v-if="modelOptions.length > 10 && !expandedSections['model_performance']"
                                @click="toggleSection('model_performance')"
                                class="explore-chip explore-chip--expand">
                                +{{ modelOptions.length - 10 }}
                            </button>
                        </div>

                        <div class="explore-filter-card__panel">
                            <div class="explore-filter-card__panel-title">Filter by model score</div>
                            <div class="explore-filter-card__panel-subtitle">Pick a score range for the selected model.</div>

                            <div class="explore-chip-column">
                                <button v-for="opt in modelScoreOptions" :key="opt.id || 'any'"
                                    :disabled="!explorerStore.filters.eval_model_name && !opt.isAny"
                                    @click="handleFilterClick('eval_score_range', opt.id)"
                                    :class="[
                                        'explore-chip',
                                        'explore-chip--block',
                                        (opt.isAny && explorerStore.filters.eval_score_range === opt.id) ? 'explore-chip--any' : '',
                                        (!explorerStore.filters.eval_model_name && !opt.isAny) ? 'explore-chip--disabled' : '',
                                        explorerStore.filters.eval_score_range === opt.id ? 'explore-chip--active' : ''
                                    ]">
                                    <span class="explore-chip__block-text">
                                        <strong>{{ opt.label }}</strong>
                                        <span class="explore-chip__muted">{{ opt.sublabel }}</span>
                                        <span v-if="explorerStore.filters.eval_score_range === opt.id" class="explore-chip__dot"></span>
                                    </span>
                                </button>
                            </div>

                            <div v-if="!explorerStore.filters.eval_model_name" class="explore-filter-card__panel-hint mt-3">
                                Select a model to enable this filter.
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Run Time TTC -->
                <div class="explore-filter-card">
                    <div class="explore-filter-card__header">
                        <div class="flex items-center gap-2">
                            <div class="text-white/95 font-semibold text-sm leading-5">Max Runtime (Test Time Compute)</div>
                            <div class="flex items-center">
                                <i class="mdi mdi-information-outline text-base cursor-help text-white">
                                    <v-tooltip activator="parent" location="top" width="550" text="The time it took to generate model responses for all tasks in the eval. Maximum time across all models in the eval leaderboard."></v-tooltip>
                                </i>
                            </div>
                        </div>
                        <div class="explore-filter-card__subtitle">Filter evals by how long a run takes</div>
                    </div>

                    <div class="explore-filter-card__body">
                        <div class="explore-chip-column">
                            <button v-for="opt in runTimeMaxOptions" :key="opt.id"
                                @click="handleFilterClick('eval_run_time_max', opt.id)"
                                :class="[
                                    'explore-chip',
                                    explorerStore.filters.eval_run_time_max === opt.id ? 'explore-chip--active' : ''
                                ]">
                                <span class="explore-chip__label explore-chip__label--sm" :title="opt.label">{{ opt.label }} <span class="explore-chip__muted">{{ opt.sublabel }}</span></span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Modalities -->
                <div class="explore-filter-card">
                    <div class="explore-filter-card__header">
                        <div class="flex items-center gap-2">
                            <div class="text-white/95 font-semibold text-sm leading-5">Modality</div>
                            <div class="flex items-center">
                                <i class="mdi mdi-information-outline text-base cursor-help text-white">
                                    <v-tooltip activator="parent" location="top" width="550" text="Note that some models do not yet accept certain modalities like images. These models will not appear on leaderboards using these modalities."></v-tooltip>
                                </i>
                            </div>
                        </div>
                        <div class="explore-filter-card__subtitle">What kind of inputs this eval uses</div>
                    </div>
                    <div class="explore-filter-card__body">
                        <div class="explore-filter-card__group">
                            <div class="explore-chip-row">
                            <template v-for="(tags, type) in groupedModalityTags" :key="type">
                                        <template v-for="(tag, index) in tags" :key="tag.id">
                                            <button v-if="index < 8 || expandedSections[type]"
                                                @click="handleFilterClick(tabToFilterKey[activeTab], tag.id, true)"
                                                :class="[
                                                    'explore-chip',
                                                    explorerStore.filters[tabToFilterKey[activeTab]]?.includes(tag.id) ? 'explore-chip--active' : ''
                                                ]"
                                                :style="{
                                                    backgroundColor: explorerStore.filters[tabToFilterKey[activeTab]]?.includes(tag.id) ? `${tag.display_color}25` : '',
                                                    borderColor: explorerStore.filters[tabToFilterKey[activeTab]]?.includes(tag.id) ? `${tag.display_color}50` : ''
                                                }">
                                                <div class="explore-chip__inner">
                                                    <i :class="[tag.display_icon ? tag.display_icon : 'uil uil-cube', 'explore-chip__icon']"
                                                       :style="{ color: tag.display_color }"></i>
                                                    <span class="explore-chip__label explore-chip__label--sm" :title="tag.name">{{ tag.name }}</span>
                                                </div>
                                            </button>
                                        </template>
                                        <button v-if="tags.length > 8 && !expandedSections[type]"
                                            @click="toggleSection(type)"
                                            class="explore-chip explore-chip--expand">
                                            +{{ tags.length - 8 }}
                                        </button>
                            </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        
            <!-- Occupations Content -->
            <div v-if="activeTab === 'occupations'" class="explore-sidebar__group">
                <div class="explore-filter-card bg-transparent border-none">
                    <div class="explore-filter-card__header">
                        <div class="flex items-center gap-2">
                            <div class="text-white/95 font-semibold text-sm leading-5">Major Groups</div>
                            <div class="flex items-center">
                                <i class="mdi mdi-information-outline text-base cursor-help text-white">
                                    <v-tooltip activator="parent" location="top" width="550" text="Major groups are the highest level of aggregation in the U.S. SOC system and is akin to an industry grouping. Click into a Major Group to see all evals for that group. Select more granular detailed occupations from that group below."></v-tooltip>
                                </i>
                            </div>
                        </div>
                        <div class="explore-filter-card__subtitle">Filter evals by real occupations in the U.S. SOC system</div>
                    </div>
                    <div class="explore-filter-card__body">
                        <div class="explore-chip-row">
                            <!-- All groups chip -->
                            <button
                                @click="handleTagTypeClick(ALL_GROUPS)"
                                :class="[
                                    'explore-chip',
                                    selectedTagType === ALL_GROUPS ? 'explore-chip--active' : ''
                                ]">
                                <div class="explore-chip__inner">
                                  
                                    <span class="explore-chip__label explore-chip__label--sm" title="All groups">
                                        All groups
                                    </span>
                                    <span v-if="selectedTagType === ALL_GROUPS" class="explore-chip__dot"></span>
                                </div>
                            </button>
                            <template v-for="tagType in tagTypes" :key="tagType.id || tagType.type || tagType">
                                <button
                                    @click="handleTagTypeClick(tagType)"
                                    :class="[
                                        'explore-chip',
                                        selectedTagType === (tagType.type || tagType.name || tagType) ? 'explore-chip--active' : ''
                                    ]">
                                    <div class="explore-chip__inner">
                            
                                        <span class="explore-chip__label explore-chip__label--sm" :title="tagType.type || tagType.name || tagType">
                                            {{ tagType.type || tagType.name || tagType }}
                                        </span>
                                        <span v-if="selectedTagType === (tagType.type || tagType.name || tagType)" class="explore-chip__dot"></span>
                                    </div>
                                </button>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Detailed Occupations Content -->
                <div class="explore-filter-card">
                    <div class="explore-filter-card__header">
                        <div class="text-white/95 font-semibold text-sm leading-5">Detailed Occupations</div>
                    </div>
                    <div class="explore-filter-card__body">
                        <div v-if="!selectedTagType" class="explore-filter-card__panel-hint">
                            Select a Major Group above to see detailed occupations.
                        </div>
                        <div v-else class="explore-chip-row">
                            <template v-for="occupation in detailedOccupations" :key="occupation.id">
                                <button
                                    @click="handleFilterClick('use_case', occupation.id, true)"
                                    :class="[
                                        'explore-chip',
                                        explorerStore.filters.use_case?.includes(occupation.id) ? 'explore-chip--active' : ''
                                    ]"
                                    :style="{
                                        backgroundColor: explorerStore.filters.use_case?.includes(occupation.id) ? `${occupation.display_color}25` : '',
                                        borderColor: explorerStore.filters.use_case?.includes(occupation.id) ? `${occupation.display_color}50` : ''
                                    }">
                                    <div class="explore-chip__inner">
                                        <i :class="[occupation.display_icon ? occupation.display_icon : 'uil uil-cube', 'explore-chip__icon']"
                                           :style="{ color: occupation.display_color }"></i>
                                        <span class="explore-chip__label explore-chip__label--sm" :title="occupation.name">{{ occupation.name }}</span>
                                    </div>
                                </button>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<style lang="sass" scoped>
@keyframes travelHighlight {
    0% {
        background-position: 0 0, 0 0, 0 0, 0 0, 0 -100%;
    }
    100% {
        background-position: 0 0, 0 0, 0 0, 0 0, 0 100%;
    }
}

:deep(.v-select) {
    .vs__dropdown-toggle {
        padding: 8px 0;
        background: transparent;
        border: none;
        min-height: 40px;
    }

    .vs__selected {
        margin: 0;
        padding-left: 0;
        color: inherit;
    }

    .vs__search {
        margin: 0;
        padding: 0;
    }

    .vs__actions {
        padding-right: 10px;
    }

    .vs__open-indicator {
        fill: currentColor;
    }
}

:deep(.sidebar-wrapper) {
    position: relative;
    min-height: 100%;
}

/* Explore page styles (Sidebar) */

.explore-sidebar {
  display: none;
  width: 25%;
  padding: 1px;
  border-radius: 22px;
  border: 1px solid #333333;
}

@media (min-width: 1024px) {
  .explore-sidebar {
    display: block;
  }
}

@keyframes exploreSidebarTravelHighlight {
  0% {
    background-position: 0 0, 0 0, 0 0, 0 0, 0 -100%;
  }

  100% {
    background-position: 0 0, 0 0, 0 0, 0 0, 0 100%;
  }
}

.explore-sidebar__panel {
  position: sticky;
  top: 88px;
  width: 100%;
  height: 100%;
  padding: 12px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  max-height: calc(100vh - 112px);
  display: flex;
  flex-direction: column;
}

.explore-sidebar__header {
  padding-bottom: 12px;
}

.explore-sidebar__header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.explore-sidebar__title {
  color: #fff;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
}

.explore-sidebar__header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.explore-sidebar__selected-count {
  padding: 2px 8px;
  color: #fff;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
}

.explore-sidebar__clear-btn {
  font-size: 12px;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  padding: 0 10px;
  height: 28px;
  color: rgba(255, 255, 255, 0.8);
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 200ms ease, background-color 200ms ease;
}

.explore-sidebar__clear-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 1);
}

.explore-sidebar__tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.explore-sidebar__tab {
  flex: 1;
  padding: 6px 8px;
  font-size: 13.7px;
  line-height: 20px;
  font-weight: 400;
  border-radius: 12px;
  transition: color 200ms ease, background-color 200ms ease;
  background: transparent;
  border: 1px solid transparent;
}

.explore-sidebar__tab--active {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #fe3e01;
  color: #fe3e01;
}

.explore-sidebar__tab--inactive {
  color: rgba(255, 255, 255, 0.6);
}

.explore-sidebar__tab--inactive:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

.explore-sidebar__content::-webkit-scrollbar {
  display: none;
}

.explore-sidebar__content {
  flex: 1;
  overflow-y: auto;
  height: 100%;
  
  margin-bottom: 12px;
}

.explore-sidebar__group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.explore-sidebar__group-title {
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  margin: 0 0 8px 0;
}

.explore-filter-card {
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0px 16px 80px rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(12px);
  padding: 12px;
}

.explore-filter-card__header {
  margin-bottom: 8px;
}


.explore-filter-card__subtitle {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.explore-filter-card__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.explore-filter-card__group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.explore-chip-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.explore-chip {
  border-radius: 9999px;
  padding: 0 12px;
  height: 32px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(4px);
  color: rgba(255, 255, 255, 0.8);
  transition: color 200ms ease, background-color 200ms ease, border-color 200ms ease;
  width: fit-content;
}

.explore-chip:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.explore-chip--active {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #fe3e01;
}

.explore-chip--disabled {
  color: rgba(255, 255, 255, 0.25);
  opacity: 0.5;
  cursor: not-allowed;
}

.explore-chip--solid {
  background: rgba(255, 255, 255, 0.1);
}

.explore-chip--solid:hover {
  background: rgba(255, 255, 255, 0.05);
}

.explore-chip__inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.explore-chip__label {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.explore-chip__label--sm {
  max-width: 200px;
}

.explore-chip__icon {
  font-size: 16px;
  display: inline-flex;
  align-items: center;
}

.explore-chip__icon-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.explore-chip__icon-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.explore-chip__icon-circle i {
  font-size: 10px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.8);
}

.explore-chip__close {
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 1);
}

.explore-chip__close:hover {
  color: rgba(255, 255, 255, 0.8);
}

.explore-chip--expand {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.explore-chip-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.explore-chip--block {
  width: 100%;
  height: 32px;
  justify-content: center;
  text-align: center;
  padding: 0 14px;
  border-radius: 9999px;
}
.explore-filter-card__panel {
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px;
}

.explore-filter-card__panel-title {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  margin-bottom: 2px;
}

.explore-filter-card__panel-subtitle {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 400;
  font-size: 11.8px;
  line-height: 16px;
  margin-bottom: 10px;
}

.explore-filter-card__panel-hint {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.explore-chip__muted {
  color: rgba(255, 255, 255, 0.55);
  font-weight: 400;
  margin-left: 6px;
}

.explore-chip--any {
  background: rgba(255, 255, 255, 0.1);
}

.explore-chip__dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #fe3e01;
  border-radius: 9999px;
  margin-left: 10px;
  vertical-align: middle;
}


.explore-chip__block-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  line-height: 18px;
  display: flex;
  align-items: center;
}

.explore-chip__block-text strong {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
}

.explore-chip--primary-active {
  color: #fff;
  border-color: rgba(254, 62, 1, 1);
  background: rgba(254, 62, 1, 0.5);
}
</style>